'use client';

/**
 * SupremeAuthProvider — child-app (iframe) authentication for the SI main app.
 *
 * This app runs embedded inside the Supreme Intelligence main app
 * (`supremegroup.ai`). The `@supreme-ai/si-sdk` `useCreditSystem` hook detects
 * the embedded context and requests a JWT from the parent window over
 * `postMessage`; standalone use falls back to the SDK login flow.
 *
 * This file is the source of truth under `customizations/supreme-sdk/`. The
 * Railway build (`customizations/railway/apply.mjs`) copies it into
 * `apps/web/src/supreme/` so it lands inside the Next build graph, and patches
 * `apps/web/app/layout.tsx` to mount it. Nothing here is committed into the
 * upstream `apps/web` tree, so rebasing onto Open Design `main` stays clean.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useCreditSystem } from '@supreme-ai/si-sdk';
import { syncSupremeCookies } from './supremeRequestContext';

// Public (non-secret) config, read server-side in `layout.tsx` from
// `process.env` and passed down as props so Railway's runtime env injection
// works (vs build-time `NEXT_PUBLIC_*` inlining).
export interface SupremeAuthConfig {
  apiBaseUrl: string;
  authUrl: string;
  agentsApiBaseUrl: string;
  allowedOrigins: string[];
  debug: boolean;
}

type CreditSystem = ReturnType<typeof useCreditSystem>;

interface SupremeOrganization {
  id?: string | number;
  name?: string;
  user_role_ids?: number[];
  roles?: Record<string, string>;
  selectedStatus?: boolean;
  isSelected?: boolean;
}

interface SupremeAuthContextValue {
  /** Authenticated SDK user, or null until the parent hands over a token. */
  user: CreditSystem['user'];
  /** Organization currently selected in the parent app. */
  organization: SupremeOrganization | null;
  organizationId: string | null;
  organizationName: string | null;
  /** Raw JWT for downstream API calls. */
  accessToken: string | null;
  /** SI API base (`SUPREME_AI_API_BASE_URL`, ends in `/api`) for direct
   *  browser → SI calls such as the SI-305 report upload. */
  apiBaseUrl: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Standalone fallback; in embedded mode the parent drives auth. */
  login: CreditSystem['login'];
  logout: CreditSystem['logout'];
  /** Full SDK surface for credits/personas/agents if needed later. */
  creditSystem: CreditSystem;
}

// Refresh the parent token well inside its lifetime; balance polling is off
// because this pass is auth-only (the SDK still authenticates regardless).
const TOKEN_REFRESH_INTERVAL = 300_000;
const PARENT_TIMEOUT = 15_000;

const SupremeAuthContext = createContext<SupremeAuthContextValue | undefined>(
  undefined,
);

function selectOrganization(
  creditSystem: CreditSystem,
): SupremeOrganization | null {
  if (creditSystem.selectedOrganization) {
    return creditSystem.selectedOrganization as SupremeOrganization;
  }
  const orgs = (creditSystem.organizations ?? []) as SupremeOrganization[];
  return (
    orgs.find((o) => o.selectedStatus === true || o.isSelected === true) ??
    orgs[0] ??
    null
  );
}

export function SupremeAuthProvider({
  config,
  children,
}: {
  config: SupremeAuthConfig;
  children: ReactNode;
}) {
  const creditSystem = useCreditSystem({
    apiBaseUrl: config.apiBaseUrl,
    agentsApiBaseUrl: config.agentsApiBaseUrl,
    authUrl: config.authUrl,
    autoInit: true,
    debug: config.debug,
    parentTimeout: PARENT_TIMEOUT,
    tokenRefreshInterval: TOKEN_REFRESH_INTERVAL,
    balanceRefreshInterval: 0,
    features: { personas: false, credits: false },
    allowedOrigins: config.allowedOrigins,
    // Notify the Supreme Group parent frame of in-app route changes so it can
    // deep-link / track the embedded app's location. No-op outside embedded mode.
    deepLinking: true,
    onTokenExpired: () => {
      if (config.debug) {
        console.error('[SupremeAuth] JWT token expired');
      }
    },
  });

  useEffect(() => {
    if (!config.debug) return;
    console.log('[SupremeAuth] state', {
      mode: creditSystem.mode,
      isAuthenticated: creditSystem.isAuthenticated,
      user: creditSystem.user ? '[authenticated]' : null,
      loading: creditSystem.loading,
    });
  }, [
    config.debug,
    creditSystem.mode,
    creditSystem.isAuthenticated,
    creditSystem.user,
    creditSystem.loading,
  ]);

  const value = useMemo<SupremeAuthContextValue>(() => {
    const organization = selectOrganization(creditSystem);
    return {
      user: creditSystem.user,
      organization,
      organizationId:
        organization?.id != null ? String(organization.id) : null,
      organizationName: organization?.name ?? null,
      accessToken: creditSystem.accessToken,
      apiBaseUrl: config.apiBaseUrl,
      isAuthenticated: creditSystem.isAuthenticated,
      isLoading: creditSystem.loading,
      login: creditSystem.login,
      logout: creditSystem.logout,
      creditSystem,
    };
  }, [creditSystem, config.apiBaseUrl]);

  // Mirror the access token + selected org into cookies synchronously during
  // render (not in an effect) so they are set before child components mount and
  // fire their first request — including preview <iframe src> loads and
  // EventSource streams, which cannot send headers. The Railway proxy reads the
  // cookies, validates the token, and injects the trusted org to the daemon.
  syncSupremeCookies(value.accessToken, value.organizationId);

  return (
    <SupremeAuthContext.Provider value={value}>
      {children}
    </SupremeAuthContext.Provider>
  );
}

export function useSupremeAuth(): SupremeAuthContextValue {
  const ctx = useContext(SupremeAuthContext);
  if (!ctx) {
    throw new Error('useSupremeAuth must be used within a SupremeAuthProvider');
  }
  return ctx;
}
